import { OpenInFull } from "@mui/icons-material";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { FillStyleExtension } from "@deck.gl/extensions";
import { GeoJsonLayer } from "@deck.gl/layers";
import centerOfMass from "@turf/center-of-mass";
import { Wind } from "lucide-react";
import {
  DispatchWithoutAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Map as MapGL,
  MapRef,
  Marker,
  NavigationControl,
  LngLatBoundsLike,
} from "react-map-gl/mapbox";
import classnames from "classnames";
import { format, parseISO } from "date-fns";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useTranslation, useLocale } from "@/lib/i18n";
import useMapViewState, {
  UseMapViewStateProps,
} from "@/features/shared/widgets/map/hooks/useMapViewState";
import * as WidgetCardStyled from "@/features/dashboard/components/widget-container-card/styles";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import { accountOwnedMapStyleUrl, knownMapboxStyles } from "@/lib/mapbox/base-layer-styles";
import {
  normalizeWarningLevel,
  WarningLevel,
  warningLevelColorMap,
} from "@/lib/warning-level";
import useLayoutStore from "@/store/useLayoutStore";
import patternUrl from "@/assets/images/striped-pattern.png";
import { defaultDomainId } from "@/lib/warning-settings.utils";
import {
  maritimeDomainsForIds,
  type MaritimeWarningDomainId,
} from "@/lib/maritime-domains";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";

import { MaritimeArea } from "./maritime-areas.queries";
import { MapRegion } from "./maritime-warnings.model";
import MaritimeAreaCategoryControl from "./maritime-area-category-control";
import WarningsRightPanel from "./warnings-right-panel";
import { formatWarningLevelLegend } from "./maritime-warnings.utils";
import styles from "./warnings-interactive-map-panel.module.css";
import badgeStyles from "./warning-level-badge.module.css";

import "mapbox-gl/dist/mapbox-gl.css";

const defaultMapCornerCoords = [
  { lat: 53.67885, lng: 3.273652 },
  { lat: 50.542863, lng: 7.279572 },
] as const;

function expandBoundsFromGeometry(
  geometry: MaritimeArea["geometry"],
  bounds: { minLng: number; maxLng: number; minLat: number; maxLat: number },
) {
  // Non-physical areas carry a null geometry and contribute no bounds.
  if (!geometry) return;

  const extend = (lng: number, lat: number) => {
    bounds.minLng = Math.min(bounds.minLng, lng);
    bounds.maxLng = Math.max(bounds.maxLng, lng);
    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
  };

  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates) {
      for (const [lng, lat] of ring) {
        extend(lng, lat);
      }
    }
    return;
  }

  for (const polygon of geometry.coordinates) {
    for (const ring of polygon) {
      for (const [lng, lat] of ring) {
        extend(lng, lat);
      }
    }
  }
}

function initialCoordsFromAreas(areas: MaritimeArea[]) {
  if (!areas.length) return [...defaultMapCornerCoords];

  const bounds = {
    minLng: Infinity,
    maxLng: -Infinity,
    minLat: Infinity,
    maxLat: -Infinity,
  };

  for (const area of areas) {
    expandBoundsFromGeometry(area.geometry, bounds);
  }

  if (
    !Number.isFinite(bounds.minLng) ||
    bounds.minLng === Infinity ||
    bounds.minLng === bounds.maxLng
  ) {
    return [...defaultMapCornerCoords];
  }

  return [
    { lat: bounds.maxLat, lng: bounds.maxLng },
    { lat: bounds.minLat, lng: bounds.minLng },
  ];
}

const worstRegionLevel = (region: MapRegion): WarningLevel =>
  region.warnings.reduce<WarningLevel>(
    (worst, w) => Math.max(worst, normalizeWarningLevel(w.level)) as WarningLevel,
    WarningLevel.Green,
  );

type WarningsInteractiveMapPanelProps = {
  regions: MapRegion[];
  enabledDomainIds: MaritimeWarningDomainId[];
  isLoading: boolean;
  hasError: boolean;
  /** @example "2026-04-15T09:12:05Z" */
  lastPublishedAt: string | null;
  /** Resolved page title (override or locale default). */
  title: string;
  /** Resolved text under the title. Hidden when blank. */
  description: string;
};

const SIDE_PANEL_PADDING_OPEN = 320;
const SIDE_PANEL_PADDING_CLOSED = 48;

const WarningsInteractiveMapPanel = ({
  regions,
  enabledDomainIds,
  isLoading,
  hasError,
  lastPublishedAt,
  title,
  description,
}: WarningsInteractiveMapPanelProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const contentLanguage = locale;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapRef>(null);
  const mapStyleUrl = accountOwnedMapStyleUrl(
    knownMapboxStyles.MonochromeLightWithoutLabels,
  );
  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>(undefined);
  const [sidePanelOpen, setSidePanelOpen] = useState(
    /* Matches `@media (max-width: 1100px)` in warnings-interactive-map-panel.module.css */
    () => window.matchMedia("(min-width: 1101px)").matches,
  );
  const [activeDomainId, setActiveDomainId] = useState<MaritimeWarningDomainId>(() =>
    defaultDomainId(enabledDomainIds),
  );

  const fitBoundsPadding = useMemo(
    () => ({
      top: 20,
      bottom: 20,
      left: 150, // space for the legend and category picker
      right: sidePanelOpen ? SIDE_PANEL_PADDING_OPEN : SIDE_PANEL_PADDING_CLOSED,
    }),
    [sidePanelOpen],
  );
  const fitBoundsRef = useRef(fitBoundsPadding);
  fitBoundsRef.current = fitBoundsPadding;

  useEffect(() => {
    if (!enabledDomainIds.includes(activeDomainId)) {
      setActiveDomainId(defaultDomainId(enabledDomainIds));
    }
  }, [activeDomainId, enabledDomainIds]);

  const enabledRegions = useMemo(
    () => regions.filter((region) => enabledDomainIds.includes(region.area.domainId)),
    [regions, enabledDomainIds],
  );

  const visibleRegions = useMemo(
    () => enabledRegions.filter((region) => region.area.domainId === activeDomainId),
    [enabledRegions, activeDomainId],
  );

  const worstLevelByDomainId = useMemo(() => {
    const worstFor = (domainId: MaritimeWarningDomainId): WarningLevel =>
      enabledRegions
        .filter((region) => region.area.domainId === domainId)
        .reduce<WarningLevel>(
          (worst, region) => Math.max(worst, worstRegionLevel(region)) as WarningLevel,
          WarningLevel.Green,
        );

    return Object.fromEntries(
      maritimeDomainsForIds(enabledDomainIds).map(({ domain }) => [
        domain.id,
        worstFor(domain.id),
      ]),
    ) as Record<MaritimeWarningDomainId, WarningLevel>;
  }, [enabledRegions, enabledDomainIds]);

  const [isFullscreen, layoutStore] = useLayoutStore((s) => s.context.isFullscreen);

  useEffect(() => {
    return () => {
      layoutStore.trigger.setFullscreen({ value: false });
    };
  }, [layoutStore]);

  const mapFitCoords = useMemo(
    () => initialCoordsFromAreas(visibleRegions.map((r) => r.area)),
    [visibleRegions],
  );

  const mapViewStateOptions = useMemo<UseMapViewStateProps>(
    () => ({
      containerRef: mapContainerRef,
      initialCoords: mapFitCoords,
      fitBoundsOptions: {
        maxZoom: 8, // Prevent zooming in closer than a province.
        padding: fitBoundsPadding,
      },
    }),
    // fitBoundsPadding omitted — panel toggle must not recalculate initial viewport.
    [mapFitCoords],
  );

  const { mapViewState } = useMapViewState(mapViewStateOptions);

  // Note:
  // Due to imperative usage of `fitBounds` (in useEffect), the padding is ref'ed.
  // Otherwise, the `fitBounds` would update whenever the padding changes and this would trigger
  // the effects to run whenever the right-side panel toggled.
  const fitBounds = useCallback((bounds: LngLatBoundsLike) => {
    mapRef.current?.fitBounds(bounds, {
      maxZoom: 8,
      padding: fitBoundsRef.current,
      duration: 300,
    });
  }, []);

  // Imperatively refit the locked map whenever the visible region set changes.
  // Using fitBounds via a ref avoids the one-render-cycle lag that a key remount
  // would cause (mapViewState is set in a useEffect and is always stale on remount).
  useEffect(() => {
    if (mapFitCoords.length < 2) return;
    const [ne, sw] = mapFitCoords;
    fitBounds([sw, ne]);
  }, [mapFitCoords, fitBounds]);

  // Clear selection when the selected area is no longer in the filtered set.
  useEffect(() => {
    if (selectedAreaId && !visibleRegions.some((r) => r.area.id === selectedAreaId)) {
      setSelectedAreaId(undefined);
    }
  }, [visibleRegions, selectedAreaId]);

  const selectedRegion = useMemo(
    () => visibleRegions.find((r) => r.area.id === selectedAreaId),
    [visibleRegions, selectedAreaId],
  );

  // Refit viewport when the selected region is less than 90% visible.
  useEffect(() => {
    const map = mapRef.current;
    if (!selectedRegion || !map) return;

    const regionBounds = {
      minLng: Infinity,
      maxLng: -Infinity,
      minLat: Infinity,
      maxLat: -Infinity,
    };
    expandBoundsFromGeometry(selectedRegion.area.geometry, regionBounds);
    if (!Number.isFinite(regionBounds.minLng)) return;

    const vp = map.getBounds();
    if (!vp) return;

    const overlapW = Math.max(
      0,
      Math.min(vp.getEast(), regionBounds.maxLng) -
        Math.max(vp.getWest(), regionBounds.minLng),
    );
    const overlapH = Math.max(
      0,
      Math.min(vp.getNorth(), regionBounds.maxLat) -
        Math.max(vp.getSouth(), regionBounds.minLat),
    );
    const overlapArea = overlapW * overlapH;
    const regionArea =
      (regionBounds.maxLng - regionBounds.minLng) *
      (regionBounds.maxLat - regionBounds.minLat);

    if (regionArea > 0 && overlapArea / regionArea < 0.9) {
      fitBounds([
        [regionBounds.minLng, regionBounds.minLat],
        [regionBounds.maxLng, regionBounds.maxLat],
      ]);
    }
  }, [selectedRegion, fitBounds]);

  const toggleAreaSelection = useCallback((areaId: string) => {
    setSidePanelOpen(true);
    setSelectedAreaId((prev) => (prev === areaId ? undefined : areaId));
  }, []);

  const mapLayers = useMemo(
    () =>
      visibleRegions.flatMap((region) =>
        makeRegionLayer(region, selectedAreaId, toggleAreaSelection),
      ),
    [visibleRegions, selectedAreaId, toggleAreaSelection],
  );

  const hasGeoJsonData = mapLayers.length > 0;

  return (
    <section className={classnames(styles.panel, isFullscreen && styles.panelFullscreen)}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
          <p className={styles.subtitle}>
            {t("dashboard.warnings.interactiveMap.subtitle", {
              date: fmtLastPublishedAt(lastPublishedAt, locale),
            })}
          </p>
        </div>
        <FullscreenToggle
          fullscreen={isFullscreen}
          toggle={() => layoutStore.trigger.toggleFullscreen()}
        />
      </header>

      <div className={styles.content}>
        <div className={styles.mapContainer} ref={mapContainerRef}>
          <div className={styles.mapControls}>
            {enabledDomainIds.length > 1 && (
              <MaritimeAreaCategoryControl
                worstLevelByDomainId={worstLevelByDomainId}
                enabledDomainIds={enabledDomainIds}
                onValueChange={setActiveDomainId}
                value={activeDomainId}
              />
            )}

            {/* Lives in the controls column so it stacks below the category picker. */}
            {(isLoading || !hasGeoJsonData || hasError) && (
              <div
                className={classnames(styles.mapEmptyState, panelCommonStyles.frostedPanel)}
              >
                {isLoading && <p>{t("dashboard.warnings.interactiveMap.mapLoading")}</p>}
                {!isLoading && hasError && (
                  <p>{t("dashboard.warnings.interactiveMap.mapLoadError")}</p>
                )}
                {!isLoading && !hasError && !hasGeoJsonData && (
                  <p>{t("dashboard.warnings.interactiveMap.noGeoJson")}</p>
                )}
              </div>
            )}
          </div>

          {mapViewState && (
            <MapGL
              ref={mapRef}
              initialViewState={mapViewState}
              attributionControl={false}
              projection={{ name: "mercator" }}
              mapStyle={mapStyleUrl}
              mapboxAccessToken={mapTileAccessToken}
              maxPitch={0}
              bearingSnap={360}
              bearing={0}
              doubleClickZoom={false}
              minZoom={4}
            >
              <DeckGLOverlay
                interleaved
                controller
                layers={mapLayers}
                getCursor={({ isHovering }: { isHovering: boolean }) =>
                  isHovering ? "pointer" : "grab"
                }
                onClick={(info) => {
                  if (!info.picked) setSelectedAreaId(undefined);
                }}
              />
              {visibleRegions.map((region) => (
                <RegionIconMarker
                  key={region.area.id}
                  region={region}
                  onToggle={toggleAreaSelection}
                />
              ))}
              <NavigationControl showCompass={false} position="bottom-right" />
            </MapGL>
          )}

          <WarningLevelLegend activeDomainId={activeDomainId} />

          <WarningsRightPanel
            isOpen={sidePanelOpen}
            onOpenChange={setSidePanelOpen}
            visibleRegions={visibleRegions}
            selectedRegion={selectedRegion}
            contentLanguage={contentLanguage}
            onSelectRegion={(id) => {
              setSidePanelOpen(true);
              setSelectedAreaId(id);
            }}
            onClose={() => setSelectedAreaId(undefined)}
          />
        </div>
      </div>
    </section>
  );
};

export default WarningsInteractiveMapPanel;

function FullscreenToggle({
  fullscreen,
  toggle,
}: {
  fullscreen: boolean;
  toggle: DispatchWithoutAction;
}) {
  return (
    <WidgetCardStyled.FullScreenButton onClick={toggle}>
      {fullscreen ? (
        <CloseFullscreenIcon fontSize="small" htmlColor="var(--navy)" />
      ) : (
        <OpenInFull fontSize="small" htmlColor="var(--navy)" />
      )}
    </WidgetCardStyled.FullScreenButton>
  );
}

const stripeSize = 32;
const STRIPE_MAPPING = { stripe: { x: 0, y: 0, width: stripeSize, height: stripeSize } };

function hexToRgb(hex: string): [number, number, number] {
  const channels = hex
    .replace("#", "")
    .match(/.{1,2}/g)
    ?.map((c) => parseInt(c, 16));
  const [r = 57, g = 135, b = 12] = channels ?? [];
  return [r, g, b];
}

function makeRegionLayer(
  region: MapRegion,
  selectedAreaId: string | undefined,
  onToggle: (areaId: string) => void,
): GeoJsonLayer[] {
  const colorHex = warningLevelColorMap[worstRegionLevel(region)];
  const isTransparent = colorHex === "transparent";
  // No-warning regions use a neutral gray so the stripe pattern remains visible.
  const [r, g, b] = isTransparent ? [180, 180, 180] : hexToRgb(colorHex);

  const noSelection = selectedAreaId === undefined;
  const isSelected = region.area.id === selectedAreaId;
  const dimmed = !noSelection && !isSelected;
  const strokeAlpha = dimmed ? 120 : 220;
  const needsStripes = isSelected && isTransparent;

  const sharedProps = {
    slot: "middle" as const,
    data: region.geometry,
    updateTriggers: {
      getFillColor: [selectedAreaId],
      getLineColor: [selectedAreaId],
    },
  };

  // Base fill — solid colour for all regions.
  // When selected, use a lighter alpha so the stripe overlay on top creates visible contrast.
  const baseAlpha = isTransparent
    ? dimmed
      ? 30
      : 55
    : isSelected
      ? 255
      : dimmed
        ? 160
        : 200;

  const baseLayer = new GeoJsonLayer({
    ...sharedProps,
    id: `warning-layer-base-${region.area.id}`,
    pickable: true,
    filled: true,
    stroked: true,
    lineWidthMinPixels: isSelected ? 3 : 2,
    getFillColor: [r, g, b, isTransparent ? 0 : baseAlpha],
    getLineColor: [15, 23, 42, strokeAlpha],
    onClick: () => onToggle(region.area.id),
  });

  // Stripe underlay, used behind transparent regions during selection
  const stripeLayer = new GeoJsonLayer({
    ...sharedProps,
    id: `warning-layer-stripe-${region.area.id}`,
    pickable: false,
    filled: needsStripes,
    getFillColor: [255, 255, 255, 255],
    extensions: [new FillStyleExtension({ pattern: true })] as never,
    fillPatternAtlas: patternUrl,
    fillPatternMapping: STRIPE_MAPPING,
    getFillPattern: "stripe",
    // Scale controls the world-space tile size in metres; ~600 gives ~10 km-wide stripes
    // at the zoom levels used for the Dutch coast, which looks well-proportioned.
    getFillPatternScale: 400,
    fillPatternMask: true,
  } as unknown as ConstructorParameters<typeof GeoJsonLayer>[0]);

  return [stripeLayer, baseLayer];
}

const LEGEND_GRAY = "rgba(0, 0, 0, 0.2)";

const LEGEND_LEVELS = [
  WarningLevel.Green,
  WarningLevel.Yellow,
  WarningLevel.Orange,
  WarningLevel.Red,
] as const;

const LEGEND_BFT_LABELS: Record<
  MaritimeWarningDomainId,
  Partial<Record<WarningLevel, string>>
> = {
  coastal: {
    [WarningLevel.Yellow]: "6-7 Bft",
    [WarningLevel.Orange]: "8-9 Bft",
    [WarningLevel.Red]: "10-12 Bft",
  },
  "north-sea": {
    [WarningLevel.Yellow]: "7-9 Bft",
    [WarningLevel.Orange]: "10-11 Bft",
    [WarningLevel.Red]: "12 Bft",
  },
};

function WarningLevelLegend({
  activeDomainId,
}: {
  activeDomainId: MaritimeWarningDomainId;
}) {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <div
      className={classnames(
        styles.legend,
        panelCommonStyles.frostedPanel,
        panelCommonStyles.mapPanel,
      )}
    >
      <p className={styles.legendTitle}>
        {t("dashboard.warnings.interactiveMap.legend.title")}
      </p>
      <div className={classnames(panelCommonStyles.panelDivider, styles.legendDivider)} />
      <ul className={styles.legendList}>
        {LEGEND_LEVELS.map((level) => {
          const color =
            level === WarningLevel.Green ? LEGEND_GRAY : warningLevelColorMap[level];
          const bft = LEGEND_BFT_LABELS[activeDomainId][level];
          return (
            <li key={level} className={styles.legendEntry}>
              <span className={badgeStyles.block}>
                <span className={badgeStyles.dot} style={{ background: color }} />
                <span className={badgeStyles.label}>
                  {formatWarningLevelLegend(level, locale)}
                </span>
              </span>
              {bft && <span className={styles.legendLabel}>{bft}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RegionIconMarker({
  region,
  onToggle,
}: {
  region: MapRegion;
  onToggle: (areaId: string) => void;
}) {
  const [lng, lat] = centerOfMass(region.geometry).geometry.coordinates;
  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="center"
      style={{ pointerEvents: "none" }}
    >
      <button className={styles.regionIcon} onClick={() => onToggle(region.area.id)}>
        <Wind size={14} />
      </button>
    </Marker>
  );
}

function fmtLastPublishedAt(date: string | null, locale: "nl" | "en"): string {
  if (date === null) {
    return "(geen informatie)";
  }

  const parsedDate = parseISO(date);

  return format(parsedDate, "d LLLL yyyy H:mm", {
    locale: getDateFnsLocale(locale),
  });
}
